import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../context/LanguageContext";

const LINKS = [
  { id: "storia", label: { it: "Storia", en: "Story" }, testid: "nav-storia-link" },
  { id: "menu", label: { it: "Menù", en: "Menu" }, testid: "nav-menu-link" },
  { id: "lombate", label: { it: "Lombate", en: "Steaks" }, testid: "nav-lombate-link" },
  { id: "recensioni", label: { it: "Recensioni", en: "Reviews" }, testid: "nav-recensioni-link" },
  { id: "galleria", label: { it: "Galleria", en: "Gallery" }, testid: "nav-galleria-link" },
  { id: "contatti", label: { it: "Contatti", en: "Contacts" }, testid: "nav-contatti-link" },
];

export const scrollToSection = (id) => {
  const target = `#${id}`;
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -70, duration: 1.4 });
  } else {
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  }
};

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={compact ? "lang-toggle-mobile" : "lang-toggle"}
      className="inline-flex rounded-full border border-[rgba(201,162,101,0.3)] overflow-hidden"
    >
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}${compact ? "-mobile" : ""}`}
          onClick={() => setLang(l)}
          className={`px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] font-cinzel transition-colors duration-300 ${
            lang === l ? "bg-[#C9A265] text-[#0D0C0A]" : "text-[#B0A89C] hover:text-[#C9A265]"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#0D0C0A] border-b border-[rgba(201,162,101,0.18)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("hero")}
          className="flex items-center group"
        >
          <Logo className="h-11 w-11 transition-transform duration-500 group-hover:scale-105" />
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] transition-colors duration-300 font-cinzel"
            >
              {l.label[lang]}
            </button>
          ))}
          <LangToggle />
          <button
            data-testid="nav-reserve-btn"
            onClick={() => go("contatti")}
            className="px-5 py-2.5 border border-[#C9A265] text-[#C9A265] text-xs uppercase tracking-[0.2em] font-cinzel rounded-full hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
          >
            {lang === "it" ? "Prenota un Tavolo" : "Book a Table"}
          </button>
        </nav>

        <div className="lg:hidden flex items-center gap-4">
          <LangToggle compact />
          <button
            data-testid="nav-mobile-toggle"
            className="text-[#FAF7F2]"
            onClick={() => setOpen(!open)}
            aria-label="Apri menu"
          >
            {open ? <X size={26} /> : <MenuIcon size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0D0C0A] border-b border-[rgba(201,162,101,0.18)] px-6 py-6 flex flex-col gap-5"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="text-left text-sm uppercase tracking-[0.25em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel"
              >
                {l.label[lang]}
              </button>
            ))}
            <button
              data-testid="nav-reserve-btn-mobile"
              onClick={() => go("contatti")}
              className="mt-2 px-5 py-3 border border-[#C9A265] text-[#C9A265] text-xs uppercase tracking-[0.2em] font-cinzel rounded-full w-fit"
            >
              {lang === "it" ? "Prenota un Tavolo" : "Book a Table"}
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
